import { gql } from 'graphql-request'

export const POLICY_COMPLIANCE_QUERY_KEYS = {
  sitePolicies: 'POLICY_GATING_SITE_POLICIES',
  sitePolicyRevisions: 'POLICY_GATING_SITE_POLICY_REVISIONS',
  jurisdictionRules: 'POLICY_GATING_JURISDICTION_RULES',
  clientPolicyCompliance: 'CLIENT_POLICY_COMPLIANCE',
  clientPolicyRelations: 'CLIENT_POLICY_RELATIONS',
  clientPolicyAgreements: 'CLIENT_POLICY_AGREEMENTS',
  paperApprovers: 'PAPER_FORM_APPROVERS',
  myApprovalPinStatus: 'MY_APPROVAL_PIN_STATUS',
  policyComplianceSettings: 'POLICY_COMPLIANCE_SETTINGS',
} as const

const SITE_POLICY_FIELDS = `
  sitePolicyId
  siteId
  name
  content
  isRequiredToAgree
  requiredForEquipmentUse
  acceptanceMethod
  signerRule
  guardianInPersonOnly
  equipmentScope
  renewalMonths
  updatedAt
  updatedByStaffUserName
`

const REVISION_FIELDS = `
  revisionId
  sitePolicyId
  siteId
  revisionNumber
  acceptanceGeneration
  status
  effectiveFrom
  name
  acceptanceMethod
  signerRule
  guardianInPersonOnly
  requiredForEquipmentUse
  equipmentScope
  renewalMonths
  publishedAt
  cancelledAt
`

const JURISDICTION_RULE_FIELDS = `
  ruleId
  ruleRevision
  jurisdictionCode
  equipmentScope
  effectiveFrom
  effectiveTo
  ageEvidenceRequired
  ageBands {
    minAge
    maxAge
    outcome
  }
  status
  notes
`

const READINESS_ITEM_FIELDS = `
  itemKey
  kind
  title
  reasonCode
  sitePolicyId
  policyRevisionId
  signerCapacity
  signerId
  dueOn
  validUntil
`

export const sitePoliciesForGatingQuery = gql`
  query PolicyGatingSitePolicies($siteId: String!) {
    sitePolicies(siteId: $siteId) {
      ${SITE_POLICY_FIELDS}
    }
  }
`

export const sitePolicyRevisionsQuery = gql`
  query PolicyGatingSitePolicyRevisions($siteId: ID!, $sitePolicyId: ID!) {
    sitePolicyRevisions(siteId: $siteId, sitePolicyId: $sitePolicyId) {
      ${REVISION_FIELDS}
    }
  }
`

export const createGatingSitePolicyMutation = gql`
  mutation PolicyGatingCreateSitePolicy($siteId: String!, $input: CreateSitePolicyInputInput!) {
    createSitePolicy(siteId: $siteId, input: $input) {
      ${SITE_POLICY_FIELDS}
    }
  }
`

export const updateGatingSitePolicyMutation = gql`
  mutation PolicyGatingUpdateSitePolicy(
    $id: String!
    $siteId: String!
    $input: SitePolicyUpdateInput!
  ) {
    updateSitePolicy(id: $id, siteId: $siteId, input: $input) {
      ${SITE_POLICY_FIELDS}
      publishResult {
        impactCount
        cancelledRevisionIds
        revision {
          ${REVISION_FIELDS}
        }
      }
    }
  }
`

export const deleteGatingSitePolicyMutation = gql`
  mutation PolicyGatingDeleteSitePolicy($id: String!, $siteId: String!) {
    deleteSitePolicy(id: $id, siteId: $siteId) {
      sitePolicyId
    }
  }
`

export const publishSitePolicyRevisionMutation = gql`
  mutation PolicyGatingPublishRevision(
    $siteId: ID!
    $sitePolicyId: ID!
    $requireReacceptance: Boolean
    $effectiveFrom: String
  ) {
    publishSitePolicyRevision(
      siteId: $siteId
      sitePolicyId: $sitePolicyId
      requireReacceptance: $requireReacceptance
      effectiveFrom: $effectiveFrom
    ) {
      impactCount
      cancelledRevisionIds
      revision {
        ${REVISION_FIELDS}
      }
    }
  }
`

export const cancelScheduledSitePolicyRevisionMutation = gql`
  mutation PolicyGatingCancelScheduledRevision($siteId: ID!, $sitePolicyId: ID!) {
    cancelScheduledSitePolicyRevision(siteId: $siteId, sitePolicyId: $sitePolicyId) {
      ${REVISION_FIELDS}
    }
  }
`

export const equipmentJurisdictionRulesQuery = gql`
  query PolicyGatingJurisdictionRules($siteId: ID!) {
    equipmentJurisdictionRules(siteId: $siteId) {
      ${JURISDICTION_RULE_FIELDS}
    }
    equipmentJurisdictionRulesInForce(siteId: $siteId) {
      ${JURISDICTION_RULE_FIELDS}
    }
  }
`

export const saveEquipmentJurisdictionRuleMutation = gql`
  mutation PolicyGatingSaveJurisdictionRule(
    $siteId: ID!
    $ruleId: ID
    $input: EquipmentJurisdictionRuleInput!
  ) {
    saveEquipmentJurisdictionRule(siteId: $siteId, ruleId: $ruleId, input: $input) {
      ${JURISDICTION_RULE_FIELDS}
    }
  }
`

export const retireEquipmentJurisdictionRuleMutation = gql`
  mutation PolicyGatingRetireJurisdictionRule($siteId: ID!, $ruleId: ID!, $reason: String!) {
    retireEquipmentJurisdictionRule(siteId: $siteId, ruleId: $ruleId, reason: $reason) {
      ${JURISDICTION_RULE_FIELDS}
    }
  }
`

export const clientPolicyComplianceQuery = gql`
  query ClientPolicyCompliance($siteId: ID!, $clientId: ID!) {
    clientPolicyCompliance(siteId: $siteId, clientId: $clientId) {
      clientId
      siteId
      ageVerificationMethod
      ageVerifiedAt
      dobVerified
      categories {
        equipmentCategory
        evaluatedSiteLocalDate
        age {
          status
          reasonCodes
        }
        policy {
          status
          reasonCodes
        }
        skinType {
          status
          reasonCodes
        }
        blockingItems {
          ${READINESS_ITEM_FIELDS}
        }
        actionableItems {
          ${READINESS_ITEM_FIELDS}
        }
        upcomingItems {
          ${READINESS_ITEM_FIELDS}
        }
      }
    }
  }
`

export const clientPolicyRelationsQuery = gql`
  query ClientPolicyRelations($siteId: ID!, $clientId: ID!) {
    clientRelations(siteId: $siteId, clientId: $clientId) {
      counterpartyClientId
      name
      type
      relationshipSubtype
      status
    }
  }
`

export const clientPolicyAgreementsQuery = gql`
  query ClientPolicyAgreements($clientId: String!, $siteId: String, $cursor: String) {
    clientAgreementsWithPagination(
      clientId: $clientId
      siteId: $siteId
      pageSize: 50
      cursor: $cursor
    ) {
      results {
        row {
          agreementId
          agreementName
          type
          status
          signedAt
          createdAt
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

export const clientAgreementsPageQuery = gql`
  query ClientAgreementsPage(
    $clientId: String!
    $siteId: String
    $filters: ClientAgreementsWithPaginationFiltersInput
    $pageSize: Float
    $cursor: String
    $search: String
  ) {
    clientAgreementsWithPagination(
      clientId: $clientId
      siteId: $siteId
      filters: $filters
      pageSize: $pageSize
      cursor: $cursor
      search: $search
    ) {
      results {
        row {
          agreementId
          agreementName
          status
          signedAt
          createdAt
          siteAgreementTemplateId
          sentAt
        }
      }
      pageInfo {
        hasNextPage
        startCursor
        endCursor
        totalRecords
      }
    }
  }
`

export const paperApproversQuery = gql`
  query PaperFormApprovers($siteId: ID!) {
    paperFormApprovers(siteId: $siteId) {
      staffUserId
      firstName
      lastName
    }
  }
`

export const myApprovalPinStatusQuery = gql`
  query MyApprovalPinStatus {
    myApprovalPinStatus {
      isSet
      lockedUntil
      setAt
    }
  }
`

export const setMyApprovalPinMutation = gql`
  mutation SetMyApprovalPin($pin: String!, $currentPin: String) {
    setMyApprovalPin(pin: $pin, currentPin: $currentPin) {
      isSet
      lockedUntil
      setAt
    }
  }
`

export const verifyMyApprovalPinMutation = gql`
  mutation VerifyMyApprovalPin($pin: String!, $siteId: ID) {
    verifyMyApprovalPin(pin: $pin, siteId: $siteId) {
      isSet
      lockedUntil
      setAt
    }
  }
`

export const verifyClientAgeMutation = gql`
  mutation VerifyClientAge(
    $siteId: ID!
    $clientId: ID!
    $dateOfBirth: String!
    $method: AgeVerificationMethod!
  ) {
    verifyClientAge(
      siteId: $siteId
      clientId: $clientId
      dateOfBirth: $dateOfBirth
      method: $method
    ) {
      clientId
      dateOfBirth
      ageVerificationMethod
      ageVerifiedAt
      capacityMismatchAgreementIds
    }
  }
`

export const createPaperScanUploadMutation = gql`
  mutation CreatePaperScanUpload($siteId: ID!, $clientId: ID!) {
    createPaperScanUpload(siteId: $siteId, clientId: $clientId) {
      uploadId
      uploadUrl
      expiresAt
      headers {
        name
        value
      }
    }
  }
`

export const recordPaperPolicyFormMutation = gql`
  mutation RecordPaperPolicyForm(
    $siteId: ID!
    $clientId: ID!
    $scanUploadId: ID!
    $approverStaffUserId: ID!
    $approverPin: String!
    $slots: [PaperPolicyFormSlotInput!]!
  ) {
    recordPaperPolicyForm(
      siteId: $siteId
      clientId: $clientId
      scanUploadId: $scanUploadId
      approverStaffUserId: $approverStaffUserId
      approverPin: $approverPin
      slots: $slots
    ) {
      paperRecordId
      acceptances {
        agreementId
        acceptanceState
        slotIndex
      }
    }
  }
`

export const withdrawElectronicConsentMutation = gql`
  mutation WithdrawElectronicConsent($siteId: ID!, $clientId: ID!, $signerId: ID!) {
    withdrawElectronicConsent(siteId: $siteId, clientId: $clientId, signerId: $signerId) {
      signerId
      withdrawnConsentIds
    }
  }
`

export const withdrawGuardianPermissionMutation = gql`
  mutation WithdrawGuardianPermission(
    $siteId: ID!
    $clientId: ID!
    $guardianSignerId: ID!
    $reason: String
  ) {
    withdrawGuardianPermission(
      siteId: $siteId
      clientId: $clientId
      guardianSignerId: $guardianSignerId
      reason: $reason
    ) {
      withdrawalId
    }
  }
`

export const voidPolicyAcceptanceMutation = gql`
  mutation VoidPolicyAcceptance($siteId: ID!, $agreementId: ID!, $reason: String!) {
    voidPolicyAcceptance(siteId: $siteId, agreementId: $agreementId, reason: $reason) {
      agreementId
      acceptanceState
    }
  }
`

export const revokeGuardianAuthorityMutation = gql`
  mutation RevokeGuardianAuthority(
    $siteId: ID!
    $clientId: ID!
    $guardianClientId: ID!
    $reason: String
  ) {
    revokeGuardianAuthority(
      siteId: $siteId
      clientId: $clientId
      guardianClientId: $guardianClientId
      reason: $reason
    ) {
      revokedRelationCount
    }
  }
`

const POLICY_COMPLIANCE_SETTINGS_FIELDS = `
  siteId
  allowQueueBeforePoliciesSigned
  autoSendPendingItemsLink
  guardianConsentAge
  allowRemoteGuardianSigning
  sendOnPlanPurchase
  sendOnHomeLocationChange
  renewalReminderDays
  eSignConsentProfile
  sendPushForPendingPolicies
`

export const sitePolicyComplianceSettingsQuery = gql`
  query SitePolicyComplianceSettings($siteId: ID!) {
    sitePolicyComplianceSettings(siteId: $siteId) {
      ${POLICY_COMPLIANCE_SETTINGS_FIELDS}
    }
  }
`

export const updateSitePolicyComplianceSettingsMutation = gql`
  mutation UpdateSitePolicyComplianceSettings(
    $siteId: ID!
    $input: PolicyComplianceSettingsInput!
  ) {
    updateSitePolicyComplianceSettings(siteId: $siteId, input: $input) {
      ${POLICY_COMPLIANCE_SETTINGS_FIELDS}
    }
  }
`
